import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PageHeader, TabBar } from "@fox1ck-org/ui";

import { useT, type TKey } from "@/lib/i18n";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { carryPeriod, tabFromPath } from "@/lib/route-tab";
import { useSearchClicks } from "@/lib/services/click-service";
import { useSearchConversions } from "@/lib/services/conversion-service";
import { useListPostbacks } from "@/lib/services/postback-service";

const ActionsSlot = createContext<HTMLElement | null>(null);

/** A tab's own controls, drawn at the right of the «Журнал» title. */
export function JournalActions({ children }: { children: ReactNode }) {
  const target = useContext(ActionsSlot);
  return target ? createPortal(children, target) : null;
}

/** How often the open journal looks for new rows (доска: «в реальном времени»). */
export const JOURNAL_POLL_MS = 15_000;

/** The page sizes every journal tab offers, 50 the default. */
export const JOURNAL_PAGE_SIZES = [50, 100, 250];

export const JOURNAL_TABS = ["clicks", "conversions", "postbacks", "bots"] as const;
export type JournalTab = (typeof JOURNAL_TABS)[number];

const TAB_LABEL: Record<JournalTab, TKey> = {
  clicks: "journal.tab.clicks",
  conversions: "journal.tab.conversions",
  postbacks: "journal.tab.postbacks",
  bots: "journal.tab.bots",
};

/**
 * «Журнал» — the rows behind the numbers (доски JClicks, JConversions,
 * JPostbacks, JBots): one title, four tabs that are addresses
 * (/journal/clicks|conversions|postbacks|bots, decision 04), each with how
 * many rows it holds over the Top bar's period — people, conversions, every
 * partner postback, bots — and the tab below. Switching a tab carries the
 * period and drops the other tab's filters.
 */
export function JournalShell() {
  const t = useT();
  const params = useTableParams({ defaultPageSize: JOURNAL_PAGE_SIZES[0], pageSizeOptions: JOURNAL_PAGE_SIZES });
  const range = { from: params.from, to: params.to, timezone: params.tz };
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // Every tab has its own segment, the first one too: /journal itself is a redirect.
  const tab = tabFromPath(pathname, "/journal", JOURNAL_TABS, "clicks");
  const setTab = (next: JournalTab) => navigate(`/journal/${next}${carryPeriod(search)}`);

  // A tab's count is its rows' total over the period. The open tab, on its
  // first page with no filter of its own, asks exactly that question — so its
  // count is the same request as its page (one query key), not another one.
  const plain = params.pageIndex === 0 && !params.search && Object.keys(params.filters).length === 0;
  const rowsOf = (k: JournalTab) => (plain && tab === k ? { limit: params.pageSize, offset: 0 } : { limit: 1, offset: 0 });
  const people = useSearchClicks({ range, trafficClass: "human", ...rowsOf("clicks") }, { refetchInterval: JOURNAL_POLL_MS });
  const convs = useSearchConversions({ range, ...rowsOf("conversions") }, { refetchInterval: JOURNAL_POLL_MS });
  const pbs = useListPostbacks({ from: params.from, to: params.to, timezone: params.tz, ...rowsOf("postbacks") }, { refetchInterval: JOURNAL_POLL_MS });
  // «Боты» reads every class with the segments for its panel whatever the table shows: the same question.
  const bots = useSearchClicks({ range, botsOnly: true, ...(tab === "bots" ? { withSegments: true } : {}), limit: 1 }, { refetchInterval: JOURNAL_POLL_MS });
  const count: Record<JournalTab, number | undefined> = {
    clicks: people.data?.total,
    conversions: convs.data?.total,
    postbacks: pbs.data?.all,
    bots: bots.data?.total,
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-5 pt-4">
        <PageHeader
          title={t("journal.title")}
          actions={<div ref={setSlot} className="flex items-center gap-3" />}
          tabs={
            <TabBar
              aria-label={t("journal.title")}
              value={tab}
              onValueChange={(v) => setTab(v as JournalTab)}
              tabs={JOURNAL_TABS.map((k) => ({ value: k, label: t(TAB_LABEL[k]), count: count[k], panelId: "journal-tab" }))}
            />
          }
        />
      </div>
      <ActionsSlot.Provider value={slot}>
        <div id="journal-tab" role="tabpanel" className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </div>
      </ActionsSlot.Provider>
    </div>
  );
}
